import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import { getRestaurantInfo } from '../../utils/restaurantInfo';
import './TopNavbar.css';

const TopNavbar = ({ onMenuClick, adminName }) => {
  const navigate = useNavigate();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [restaurantLogo, setRestaurantLogo] = useState(null);
  const [restaurantName, setRestaurantName] = useState('Restaurant');
  const notificationRef = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Fetch restaurant info (logo and name)
  useEffect(() => {
    const loadRestaurantInfo = async () => {
      try {
        const { data, error } = await getRestaurantInfo();
        if (!error && data) {
          if (data.logo_url) {
            setRestaurantLogo(data.logo_url);
          }
          if (data.name) {
            setRestaurantName(data.name);
          }
        }
      } catch (error) {
        console.error('Error loading restaurant info:', error);
      }
    };
    loadRestaurantInfo();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotificationDropdown(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const readNotifications = JSON.parse(localStorage.getItem('readNotifications') || '[]');
      
      // Fetch new orders (pending status)
      const { data: newOrders } = await supabase
        .from('orders')
        .select('id, customer_name, total, created_at')
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(10);

      // Fetch new contact messages
      const { data: newMessages } = await supabase
        .from('contact_messages')
        .select('id, name, subject, created_at')
        .eq('status', 'new')
        .order('created_at', { ascending: false })
        .limit(10);

      // Fetch new reviews (if reviews table exists)
      let newReviews = [];
      try {
        const { data: reviews } = await supabase
          .from('reviews')
          .select('id, rating, comment, created_at')
          .eq('is_approved', false)
          .order('created_at', { ascending: false })
          .limit(10);
        newReviews = reviews || [];
      } catch (e) {
        // Reviews table might not exist, ignore
      }

      // Combine all notifications
      const allNotifications = [
        ...(newOrders || []).map(order => ({
          id: `order-${order.id}`,
          type: 'order',
          title: 'New Order',
          message: `Order from ${order.customer_name} - PKR ${parseFloat(order.total).toFixed(2)}`,
          time: new Date(order.created_at),
          link: '/admin/orders',
          data: order
        })),
        ...(newMessages || []).map(message => ({
          id: `message-${message.id}`,
          type: 'message',
          title: 'New Contact Message',
          message: `${message.name}: ${message.subject || 'No subject'}`,
          time: new Date(message.created_at),
          link: '/admin/contact',
          data: message
        })),
        ...(newReviews || []).map(review => ({
          id: `review-${review.id}`,
          type: 'review',
          title: 'New Review',
          message: `${review.rating}⭐ - ${review.comment?.substring(0, 50) || 'No comment'}...`,
          time: new Date(review.created_at),
          link: '/admin/reviews',
          data: review
        }))
      ].sort((a, b) => b.time - a.time).slice(0, 20);

      // Filter out read notifications
      const unreadNotifications = allNotifications.filter(
        notif => !readNotifications.includes(notif.id)
      );

      setNotifications(allNotifications);
      setNotificationCount(unreadNotifications.length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = (notificationId) => {
    const readNotifications = JSON.parse(localStorage.getItem('readNotifications') || '[]');
    if (!readNotifications.includes(notificationId)) {
      readNotifications.push(notificationId);
      localStorage.setItem('readNotifications', JSON.stringify(readNotifications));
      setNotificationCount(prev => Math.max(0, prev - 1));
    }
  };

  const markAllAsRead = () => {
    const allIds = notifications.map(n => n.id);
    localStorage.setItem('readNotifications', JSON.stringify(allIds));
    setNotificationCount(0);
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    setShowNotificationDropdown(false);
    navigate(notification.link);
  };

  const formatTime = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const handleLogout = () => {
    localStorage.removeItem('adminSession');
    sessionStorage.removeItem('adminSession');
    window.location.href = '/admin';
  };

  const getInitials = (name) => {
    if (!name) return 'A';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order': return '🛒';
      case 'message': return '✉️';
      case 'review': return '⭐';
      default: return '🔔';
    }
  };

  return (
    <nav className="top-navbar">
      <div className="navbar-left">
        <button className="hamburger-menu" onClick={onMenuClick} aria-label="Menu">
          ☰
        </button>
        <div 
          className="admin-logo-container"
          onClick={() => navigate('/admin/dashboard')}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', marginLeft: '16px' }}
        >
          {restaurantLogo ? (
            <img 
              src={restaurantLogo} 
              alt={restaurantName}
              className="admin-header-logo"
            />
          ) : (
            <span style={{ 
              fontFamily: 'var(--font-heading)', 
              fontSize: '18px', 
              fontWeight: 700, 
              color: 'var(--text-primary)' 
            }}>
              {restaurantName}
            </span>
          )}
        </div>
      </div>

      <div className="navbar-right">
        <div className="notification-wrapper" ref={notificationRef}>
          <button 
            className="notification-btn" 
            aria-label="Notifications"
            onClick={() => setShowNotificationDropdown(!showNotificationDropdown)}
          >
            <span className="notification-icon">🔔</span>
            {notificationCount > 0 && (
              <span className="notification-badge">{notificationCount}</span>
            )}
          </button>

          {showNotificationDropdown && (
            <div className="notification-dropdown">
              <div className="notification-header">
                <h3>Notifications</h3>
                {notificationCount > 0 && (
                  <button className="mark-all-read" onClick={markAllAsRead}>
                    Mark all as read
                  </button>
                )}
              </div>
              <div className="notification-list">
                {notifications.length === 0 ? (
                  <div className="notification-empty">
                    <p>No new notifications</p>
                  </div>
                ) : (
                  notifications.map((notification) => {
                    const isRead = JSON.parse(localStorage.getItem('readNotifications') || '[]').includes(notification.id);
                    return (
                      <div
                        key={notification.id}
                        className={`notification-item ${!isRead ? 'unread' : ''}`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="notification-icon-small">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="notification-content">
                          <div className="notification-title">{notification.title}</div>
                          <div className="notification-message">{notification.message}</div>
                          <div className="notification-time">{formatTime(notification.time)}</div>
                        </div>
                        {!isRead && <div className="notification-dot"></div>}
                      </div>
                    );
                  })
                )}
              </div>
              {notifications.length > 0 && (
                <div className="notification-footer">
                  <button onClick={() => navigate('/admin/orders')}>View All</button>
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className="profile-section"
          ref={profileRef}
          onMouseEnter={() => setShowProfileDropdown(true)}
          onMouseLeave={() => setShowProfileDropdown(false)}
        >
          <div className="profile-avatar">
            {getInitials(adminName)}
          </div>
          <span className="profile-name">{adminName || 'Admin'}</span>
          <span className="dropdown-arrow">▼</span>

          {showProfileDropdown && (
            <div className="profile-dropdown">
              <button className="dropdown-item" onClick={() => setShowProfileDropdown(false)}>
                Profile
              </button>
              <button className="dropdown-item" onClick={handleLogout}>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default TopNavbar;

